import {validCoupon} from '@atoms';
import {useDiscountConfig} from '@helpers/discounts';
import React, {useCallback, useEffect, useState} from 'react';
import {useRecoilValue, useSetRecoilState} from 'recoil';

import './style.css';

const Loader = () => (
  <div className='discountspinner' />
);

const AutoLoader = () => (
  <div className='discountrollback'>שחזור</div>
);

const InputCoupon = ({imageCount, netPrice}) => {
  const [isFocus, setFocus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState('');
  const [status, setStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isVisibleCoupon, setIsVisibleCoupon] = useState(false);
  // Add a state variable to track if user has explicitly removed the coupon
  const [userRemovedCoupon, setUserRemovedCoupon] = useState(false);
  const selectedCoupon = useRecoilValue(validCoupon);
  const setCoupon = useSetRecoilState(validCoupon);
  // Get dynamic discount config
  const { discountConfig, defaultCoupon } = useDiscountConfig();
  // Find the minimum images required for auto-discount
  const minImagesForAutoDiscountAvailable = Object.keys(discountConfig)
    .sort((a, b) => Number(a) - Number(b)).find(key => discountConfig[key].enableDefault);
    
  // Helper function to find the closest lower tier with enableDefault: true
  const findClosestLowerTier = (currentTier) => {
    return Object.keys(discountConfig)
      .map(key => Number(key))
      .filter(key => key < currentTier && discountConfig[key].enableDefault)
      .sort((a, b) => b - a)[0]; // Sort descending and take the first (highest) value
  };

  const validateCoupon = async (value) => {
    if (value === '') {
      setStatus('');
      setCoupon(null);
      return;
    }

    // Find the maximum discount tier available for the current image count
    const maxDiscountAmountAvailable = Object.keys(discountConfig)
      .sort((a, b) => Number(b) - Number(a))
      .find((key) => Number(key) <= imageCount);
    
    // Handle fallbackToLower flag for tiers that should use a lower tier's discount
    if (value === defaultCoupon && maxDiscountAmountAvailable) {
      const currentTierConfig = discountConfig[maxDiscountAmountAvailable];
      
      // If the current tier has fallbackToLower enabled, find the closest lower tier
      if (currentTierConfig.fallbackToLower && !currentTierConfig.enableDefault) {
        const lowerTier = findClosestLowerTier(Number(maxDiscountAmountAvailable));
        
        if (lowerTier) {
          // Store the fallback information in localStorage
          localStorage.setItem('fallbackTierApplied', JSON.stringify({
            originalTier: maxDiscountAmountAvailable,
            fallbackTier: lowerTier,
            timestamp: Date.now()
          }));
          
          const discount = discountConfig[lowerTier].discount;
          const finalPrice = netPrice * (discount / 100);
          
          setStatus('valid');
          // Add isFallback flag to identify fallback coupons
          setCoupon({
            coupon: value,
            discount,
            discountPrice: finalPrice,
            isFallback: true
          });
          return;
        }
      }
      
      // Standard case - use the current tier if enableDefault is true
      if (currentTierConfig.enableDefault) {
        const discount = currentTierConfig.discount;
        const finalPrice = netPrice * (discount / 100);

        setStatus('valid');
        setCoupon({coupon: value, discount, discountPrice: finalPrice, source: 'static'});
        return;
      }
    }

    // Collect all coupons, handling both array and object structures
    const allCoupons = Object.keys(discountConfig).flatMap((key) => {
      const config = discountConfig[key];
      if (Array.isArray(config)) {
        return config.flatMap(item => item.coupons);
      }
      return config.coupons;
    });

    if (!allCoupons.includes(value)) {
      // NOT IN STATIC CONFIG - Check MongoDB
      try {
        // Removed console.log for production
        const userId = localStorage.getItem("uniqueUserId") || "guest";

        const response = await fetch("/api/coupon/validate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code: value,
            userId: userId,
            imageCount: imageCount,
            netPrice: netPrice
          })
        });

        const data = await response.json();
        // Removed console.log for production

        if (data.valid) {
          setStatus("valid");
          setCoupon({
            coupon: data.coupon.code,
            discount: data.coupon.discount,
            discountPrice: data.coupon.discountPrice,
            includesShipping: data.coupon.includesShipping,
            source: "mongodb",
            ...(data.coupon.discountType === "giftcard" ? { giftCard: true } : {})
          });
        } else {
          setStatus("invalid");
          setErrorMessage(data.message || 'קוד קופון לא תקין');
          setCoupon(null);
        }
      } catch (error) {
        // Removed console.error for production
        setStatus("invalid");
        setErrorMessage('שגיאה באימות הקופון');
        setCoupon(null);
      }
      return;
    }

    // Check if it's in static config
    if (allCoupons.includes(value)) {
      setStatus('invalid');
      setCoupon(null);
      return;
    }

    // Find which discount configuration contains the entered coupon code
    const requestedDiscountKey = Object.keys(discountConfig)
      .find((key) => {
        const config = discountConfig[key];
        if (Array.isArray(config)) {
          return config.some(item => item.coupons.includes(value));
        }
        return config.coupons.includes(value);
      });

    // Get the specific discount configuration for the coupon
    const configEntry = discountConfig[requestedDiscountKey];
    
    // If it's an array, find the specific config that contains the coupon
    const discountConfigByCoupon = Array.isArray(configEntry)
      ? configEntry.find(item => item.coupons.includes(value))
      : configEntry;

    // Extract the numeric part of the key for comparison
    const keyNumber = parseFloat(requestedDiscountKey);
    
    // For keys like "12.1", we want to check against the integer part (12)
    const baseNumber = Math.floor(keyNumber);
    
    if (discountConfigByCoupon.exactAmount && imageCount !== baseNumber) {
      setStatus('invalid');
      setCoupon(null);
      return;
    }

    if (imageCount < baseNumber) {
      setStatus('invalid');
      setCoupon(null);
      return;
    }

    const discount = discountConfigByCoupon.discount;
    const finalPrice = netPrice * (discount / 100);

    setStatus('valid');
    setCoupon({coupon: value, discount, discountPrice: finalPrice, source: 'static'});
  };

  const handleConfirm = async () => {
    setLoading(true);
    setErrorMessage(''); // Clear previous error
    await validateCoupon(value);
    setLoading(false);
  };

  const handleAutoConfirm = async () => {
    setLoading(true);
    setErrorMessage(''); // Clear previous error
    await validateCoupon(defaultCoupon);
    setLoading(false);
  };

  const handleFocus = () => {
    setFocus(null);
  };

  const handleBlur = (e) => {
    setFocus(false);
  };

  const removeCoupon = useCallback(() => {
    setLoading(true);
    // Set flag when user explicitly removes coupon
    setUserRemovedCoupon(true);

    // Clear the persisted coupon on the server-side cart so we don't record
    // usage for a coupon the user backed out of. Only matters for MongoDB
    // coupons (static/fallback ones aren't tracked server-side).
    if (selectedCoupon?.source === 'mongodb') {
      const userId = localStorage.getItem('uniqueUserId');
      if (userId) {
        fetch('/api/coupon/clear', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId })
        }).catch(() => { /* non-blocking */ });
      }
    }

    // If this was a fallback coupon, update localStorage
    if (selectedCoupon?.isFallback) {
      // Get the fallback information
      const fallbackInfo = localStorage.getItem('fallbackTierApplied');
      
      if (fallbackInfo) {
        // Store that user has removed this fallback
        const fallbackRemoved = localStorage.getItem('fallbackTiersRemoved') || '{}';
        const parsedRemoved = JSON.parse(fallbackRemoved);
        const { originalTier } = JSON.parse(fallbackInfo);
        
        parsedRemoved[originalTier] = true;
        localStorage.setItem('fallbackTiersRemoved', JSON.stringify(parsedRemoved));
        
        // Remove the applied fallback
        localStorage.removeItem('fallbackTierApplied');
      }
    }
    
    setTimeout(() => {
      setLoading(false);
      setCoupon(null);
      setStatus('removed');
      setValue('');
    }, 1500);
  }, [setCoupon, selectedCoupon]);

  // The server keeps a box-entered coupon / gift card on the cart and spends it at
  // payment. When this slot doesn't hold it (page reload, tier discount replaced it),
  // drop it there too, or a full-price order would still use up the gift card.
  useEffect(() => {
    if (selectedCoupon?.source === 'mongodb') return;
    const userId = localStorage.getItem('uniqueUserId');
    if (!userId) return;
    fetch('/api/coupon/clear', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId })
    }).catch(() => { /* non-blocking */ });
  }, [selectedCoupon?.source]);

  // Calculate discount amount for display
  const discount = netPrice * (selectedCoupon?.discount / 100);

  // Auto-apply fallback discount when imageCount is 9
  useEffect(() => {
    // Check if this tier has fallbackToLower enabled
    const currentTierConfig = discountConfig[imageCount];
    const hasFallbackToLower = currentTierConfig && currentTierConfig.fallbackToLower;
    
    // Check if user has previously removed this fallback
    const fallbackRemoved = localStorage.getItem('fallbackTiersRemoved');
    const parsedRemoved = fallbackRemoved ? JSON.parse(fallbackRemoved) : {};
    const wasRemoved = parsedRemoved[imageCount];
    
    // Only auto-apply when:
    // 1. The tier has fallbackToLower enabled
    // 2. No coupon is selected
    // 3. Not loading
    // 4. User hasn't explicitly removed this fallback
    // 5. User hasn't removed this specific fallback tier before
    if (hasFallbackToLower && !selectedCoupon && !loading && !userRemovedCoupon && !wasRemoved) {
      // Removed console.log for production
      // Make the coupon input visible to show the applied discount
      setIsVisibleCoupon(true);
      // Apply the default coupon which will trigger the fallbackToLower logic
      validateCoupon(defaultCoupon);
    }
  }, [imageCount, selectedCoupon, loading, validateCoupon, netPrice, setIsVisibleCoupon, userRemovedCoupon]);

  // Reset the userRemovedCoupon flag when cart quantity changes
  useEffect(() => {
    setUserRemovedCoupon(false);
  }, [imageCount]);

  useEffect(() => {
    if (selectedCoupon) {
      setIsVisibleCoupon(true);
      setStatus('valid');
    } else if (status !== "removed" && status !== "invalid") {
      setStatus('');
    }
  }, [status, selectedCoupon]);

  return isVisibleCoupon ? (
    <div className="input-group-coupon-container">
      <div className="input-group-coupon">
        <div className="action-container">
          {loading ? (
            <Loader />
          ) : (
            <>
              {status === 'valid' ? (
                <button type="button" className=" remove-btn" onClick={removeCoupon}>
                  הסר
                </button>
              ) : value === '' && imageCount >= minImagesForAutoDiscountAvailable ? (
                <button type="button" className=" check-btn" onClick={handleAutoConfirm}>
                  <AutoLoader />
                </button>
              ) : (
                <button type="button" className=" check-btn" onClick={handleConfirm}>
                  הפעל
                </button>
              )}
            </>
          )}
        </div>
        {status === 'valid' ? (
          <div className="valid-container">
            <div className="valid-container-text">
              <div className="valid-container-text-row">
                <p className="first-text-coupon first-text">פעיל</p>
                <p className="first-text-coupon">{selectedCoupon?.coupon}</p>
              </div>
              <p className="second-text">{selectedCoupon?.giftCard
                ? `${Math.round(selectedCoupon.discountPrice)} שח חסכון (כרטיס מתנה)`
                : `${Math.ceil(discount)} שח חסכון (${selectedCoupon?.discount}% הנחה)`}</p>

            </div>
            <img
              className="valid-icon"
              src="/assets/file/images/success_icon.svg"
              alt="coupon icon"
            />
          </div>
        ) : (
          <>
            <input
              className={`coupon-input ${status === 'invalid' ? 'invalid-coupon-input' : ''}`}
              value={value}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();
                setStatus('');
                setErrorMessage('');
                setValue(value);
              }}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder={isFocus ? '' : 'יש לי כרטיס מתנה'}
              disabled={status === 'valid'}
            />

            <div className="input-group-adornment ">
              <span
                className={`coupon-input-group-text ${status === 'invalid' ? 'invalid-coupon-input-group-text' : ''
                }
              ${status === 'valid' ? 'valid-coupon-input-group-text' : ''}`}
              >
                {status === 'valid' ? (
                  <img src="/assets/file/images/success_icon.svg" alt="coupon icon" />
                ) : (
                  <>
                    <div className="coupon-divider" />
                    <img src="/assets/file/images/Coupon_icon.svg" alt="coupon icon" />
                  </>
                )}
              </span>
            </div>
          </>
        )}
      </div>
      {status === 'invalid' && (
        <div className="error-text-container">
          <p className="error-text">{errorMessage || 'הקופון שהוזן לא קיים או לא בתוקף'}</p>
          <img src="/assets/file/images/alert-error-icon.svg" alt="coupon icon" />
        </div>
      )}
      {status === 'removed' && (
        <div className="remove-text">
          <p>הקופון הוסר</p>
        </div>
      )}
      {status !== 'removed' && status !== 'invalid' ? <div className="info-text" /> : null}
    </div>
  ) : (
    <div className="show-coupon-container">
      <button className="show-coupon-btn" onClick={() => setIsVisibleCoupon(true)}> יש לי כרטיס מתנה</button>
      <img className="show-coupon-img" src="/assets/file/images/Coupon_icon.svg" alt="coupon icon" />
    </div>
  );
};

export default InputCoupon;
